/**
 * The notification bell — Step 125.
 *
 * Step 118 decides who is told; this is where they read it. A stream of what
 * happened to YOUR tickets and YOUR group, with an unread count — the thing
 * that replaces "open the queue and look" as the way an agent learns somebody
 * replied.
 *
 * ── Opening does not mark everything read ──────────────────────────────
 *
 * A bell that clears on open is a bell that loses the one you glanced at while
 * reaching for something else. Reading one marks that one; "Mark all as read"
 * is an explicit act. The reference does the same.
 *
 * ── Polling, not a socket ──────────────────────────────────────────────
 *
 * Step 60's realtime channel carries TICKET invalidation, not per-user
 * delivery, and giving the bell its own socket topic means an authorisation
 * surface per user for something a 60-second poll answers. A notification that
 * arrives within a minute is indistinguishable from instant for this purpose;
 * the SLA clock is the thing that needs to be live.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { subscribe } from '../../lib/realtime';
import { useNavigate } from 'react-router-dom';
import { Bell, BellRing } from 'lucide-react';
import { Button } from '../ui/button';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { fullTime } from '../../lib/time';
import { cn } from '../../lib/utils';
import {
  browserNotificationState,
  desktopNotificationsEnabled,
  requestBrowserNotifications,
  setDesktopNotificationsEnabled,
  useBrowserNotifications,
  useNotificationPermission,
} from '../../features/notifications/useBrowserNotifications';

interface Row {
  id: string;
  event: string;
  ticketId: string | null;
  actorName: string | null;
  title: string;
  body: string;
  readAt: number | null;
  createdAt: number;
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const feed = useQuery({
    queryKey: qk.notifications,
    queryFn: () => apiFetch<{ entries: Row[]; unread: number }>('/api/notifications'),
    // The FALLBACK, not the mechanism — Step 60's socket below is what makes a
    // notification arrive without a refresh. This is what keeps the bell
    // working when the socket is down.
    refetchInterval: 60_000,
    /**
     * Keep polling while the tab is in the BACKGROUND — and this is a bug fix,
     * not a preference.
     *
     * TanStack pauses `refetchInterval` on a hidden tab by default. Step 138's
     * desktop notification only fires WHEN the tab is hidden. Left at the
     * default the two rules cancel out exactly: the only moment a desktop
     * notification is allowed to fire is the only moment we never look for one,
     * so it could never fire at all. Found by testing it rather than by reading
     * it (Kai: *"I have to refresh to see notifications"*).
     */
    refetchIntervalInBackground: true,
  });

  /**
   * Arrive without a refresh — Step 60's org firehose.
   *
   * Kai, on testing: *"I can't just get them, send actively, and reloaded
   * etc."* A minute is too long when you are waiting for one, and the fallback
   * poll was doing all the work.
   *
   * The `org` topic already exists for exactly this — it is what the queue and
   * dashboard listen on so a ticket somebody else raises appears without a
   * refresh — so this needs no new topic and no new authorisation surface. It
   * is safe by construction: the server prefixes the socket's OWN org, so
   * "the whole org" can only ever mean your own.
   *
   * It carries no notification content, and must not: it is a signal that
   * something happened, and the refetch is what decides whether any of it was
   * addressed to you. That is the same rule the rest of the socket follows,
   * and it is why a signal about a ticket you cannot see teaches you nothing.
   */
  useEffect(
    () =>
      subscribe('org', {
        onChange: () => void qc.invalidateQueries({ queryKey: qk.notifications }),
      }),
    [qc],
  );

  const markRead = useMutation({
    mutationFn: (id?: string) => apiSend('/api/notifications/read', 'POST', id ? { id } : {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.notifications }),
  });

  const unread = feed.data?.unread ?? 0;
  const rows = feed.data?.entries ?? [];

  /**
   * Step 138 — the same feed, on the desktop.
   *
   * Driven off the rows this component already polls rather than a channel of
   * its own: one source means the bell and the desktop notification can never
   * disagree about what happened, and it costs no extra request.
   */
  const [permission, setPermission] = useNotificationPermission();
  const [desktopOn, setDesktopOn] = useState(() => desktopNotificationsEnabled());
  useBrowserNotifications(rows, (ticketId) => navigate(`/tickets/${ticketId}`), feed.isSuccess);

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        onClick={() => setOpen((o) => !o)}
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          // 99+ rather than a number that stretches the badge. Past a hundred
          // the exact figure stops being information and starts being a shape.
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </Button>

      {open && (
        <>
          {/* Click-away. A plain overlay rather than a document listener: it
              cannot miss a click that lands on something which stops
              propagation. */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          {/**
           * Step 171 — a sheet on a phone, a popover on a desktop.
           *
           * Kai: *"notifications on mobile don't work nicely, they're off
           * screen."* It was `absolute right-0` with `max-w-[calc(100vw-1rem)]`,
           * and the max-width is the part that looks like it should have
           * handled it. It does not: capping the WIDTH does not move the
           * ANCHOR. The panel's right edge is pinned to the bell, which sits
           * inset from the edge of the screen, so a panel as wide as the
           * viewport runs off the left by however far the bell is inset.
           *
           * Below `sm` it is therefore pinned to the viewport instead —
           * `fixed`, a gutter each side, under the top bar — which is what a
           * panel this size should be on a phone anyway. From `sm` up the
           * original anchored popover returns unchanged.
           */}
          <div className="fixed inset-x-2 top-14 z-50 max-h-[75vh] overflow-y-auto rounded-lg border bg-card shadow-xl sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-1 sm:max-h-none sm:w-[26rem] sm:overflow-visible">
            <div className="flex items-center gap-2 border-b px-3 py-2">
              <span className="text-sm font-semibold">Notifications</span>
              <div className="flex-1" />
              {unread > 0 && (
                <button
                  onClick={() => markRead.mutate(undefined)}
                  className="rounded text-xs text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  Mark all as read
                </button>
              )}
            </div>

            {/* The permission is asked HERE, on a click, with the reason
                beside it — never on page load. Chrome ignores the request
                outside a user gesture, and a dismissed prompt is sticky: the
                site cannot ask again. Spending that one chance on somebody who
                does not yet know what the notifications are for is how a
                desk ends up unable to turn them on at all. */}
            {permission === 'default' && (
              <button
                onClick={() => void requestBrowserNotifications().then(setPermission)}
                className="flex w-full items-start gap-2 border-b bg-brand/5 px-3 py-2 text-left hover:bg-brand/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <BellRing className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                <span>
                  <span className="block text-xs font-medium">Show these on your desktop</span>
                  <span className="block text-xs text-muted-foreground">
                    So you hear about a ticket while this tab is in the background.
                  </span>
                </span>
              </button>
            )}
            {permission === 'granted' && (
              <label className="flex cursor-pointer items-center gap-2 border-b px-3 py-1.5 hover:bg-accent/40">
                <input
                  type="checkbox"
                  checked={desktopOn}
                  onChange={(e) => {
                    setDesktopNotificationsEnabled(e.target.checked);
                    setDesktopOn(e.target.checked);
                  }}
                  className="h-3.5 w-3.5 rounded border-input"
                />
                <span className="text-xs text-muted-foreground">Show these on my desktop</span>
              </label>
            )}
            {/* Said plainly rather than left as silence: a blocked permission
                looks identical to a feature that does not work. */}
            {permission === 'denied' && browserNotificationState() === 'denied' && (
              <p className="border-b px-3 py-1.5 text-xs text-muted-foreground">
                Desktop notifications are blocked for this site in your browser settings.
              </p>
            )}

            <div className="max-h-[26rem] overflow-y-auto p-1.5">
              {rows.length === 0 && (
                <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                  Nothing yet. You will hear when a ticket of yours changes.
                </p>
              )}
              {rows.map((n) => (
                <button
                  key={n.id}
                  onClick={() => {
                    if (!n.readAt) markRead.mutate(n.id);
                    setOpen(false);
                    if (n.ticketId) navigate(`/tickets/${n.ticketId}`);
                  }}
                  className={cn(
                    'flex w-full flex-col gap-0.5 rounded-md px-2.5 py-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    // Unread is weight plus a dot, never colour alone.
                    !n.readAt && 'bg-brand/5',
                  )}
                >
                  <span className="flex items-center gap-1.5">
                    {!n.readAt && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />}
                    <span className={cn('text-xs', n.readAt ? 'text-muted-foreground' : 'font-semibold')}>
                      {n.title}
                    </span>
                  </span>
                  <span className="line-clamp-2 text-sm">{n.body}</span>
                  <span className="text-xs text-muted-foreground">
                    {n.actorName ? `${n.actorName} · ` : ''}
                    {fullTime(n.createdAt)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
